import {
  GridDensity,
  GridFilterModel,
  GridRowSelectionModel,
  GridSortModel,
  useGridApiRef,
} from '@mui/x-data-grid-premium'
import { useQueryClient } from '@tanstack/react-query'
import GlobalDataGrid from 'components/DataGrid/GlobalDataGrid'
import GlobalGridToolbar from 'components/DataGrid/GlobalGridToolbar'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Lead } from 'types'
import { useBatchDeleteLeads } from '../../api/aws/leads/useBatchDeleteLeads'
import useMassUpdateLeads from '../../api/aws/leads/useMassUpdateLeads'
import { useDeleteMutation } from '../../api/aws/useDeleteMutation'
import { useLeads } from '../../api/aws/useLeads'
import { useUserItems } from '../../api/aws/users/useUserItems'
import { useLayoutContext } from '../../context/LayoutProvider/LayoutProvider'
import { useSnackbarContext } from '../../context/SnackbarProvider/SnackbarProvider'
import { renderLeadsColumns } from '../DataGrid/createColumns/renderLeadsColumns'
import { defaultVisibilityModel } from './grid/defaultModels'
import { useProcessLeadRowUpdate } from './grid/useProcessLeadRowUpdate'
import LeadDrawer from './LeadDrawer'
import { LeadsGridFooter } from './LeadsGridFooter'
import LeadsPopover from './LeadsPopover'
import { gridContainerStyles } from './style'

const defaultFilterModel: GridFilterModel = {
  items: [],
  quickFilterValues: [],
}
const defaultSortModel: GridSortModel = [{ field: 'submittedDate', sort: 'desc' }]

const LeadsPage = () => {
  const { pinned } = useLayoutContext()
  const { showSnackbar } = useSnackbarContext()
  const queryClient = useQueryClient()

  // Data fetching
  const { leads: allLeads, isLeadsLoading } = useLeads(true)
  const { data: users = [] } = useUserItems()
  const leads = useMemo(() => {
    if (!allLeads) return []
    return allLeads?.map(l => ({ ...l, rowId: l?.lead_uuid }))
  }, [allLeads])

  // Selection state
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>({
    ids: new Set(),
    type: 'include',
  })
  const [selectedLeads, setSelectedLeads] = useState<Lead[]>([])
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null)
  const [selectedMassData, setSelectedMassData] = useState<Lead[]>([])

  // Grid state
  const apiRef = useGridApiRef()
  const [density, setDensity] = useState<GridDensity>(() => {
    const saved = localStorage.getItem('leadDensityModel') as GridDensity | null
    return saved ?? 'standard'
  })

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false)

  // Popover state
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [isMassDeleteOpen, setIsMassDeleteOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  // Mass update state
  const [massUpdateData, setMassUpdateData] = useState<any>({})

  // Mutations
  const { mutate: deleteItem } = useDeleteMutation()
  const { handleMassUpdate, isUpdating } = useMassUpdateLeads()
  const { mutate: batchDeleteLeads } = useBatchDeleteLeads()

  // Inline edit row update handler
  const { processRowUpdate } = useProcessLeadRowUpdate({ users })

  // Update selected lead when selection changes
  useEffect(() => {
    if (leads && Array.from(selectionModel?.ids).length === 1) {
      const newLead = leads.find(lead => lead.lead_uuid === Array.from(selectionModel?.ids)?.[0])
      setSelectedLead(newLead || null)
    } else {
      setSelectedLead(null)
    }
  }, [selectionModel, leads])

  const handleMassUpdateChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setMassUpdateData((prev: any) => ({ ...prev, [name]: value }))
  }, [])

  const handleUpdate = useCallback(
    (massUpdateData: any, rowSelectionModel: GridRowSelectionModel) => {
      handleMassUpdate(massUpdateData, rowSelectionModel)
    },
    [handleMassUpdate]
  )

  const handleClearMassUpdate = useCallback(() => {
    setMassUpdateData({})
  }, [])

  const handleDeleteClick = useCallback(
    (event: React.MouseEvent<HTMLElement>, rowSelectionModel: GridRowSelectionModel) => {
      const ids = Array.from(rowSelectionModel?.ids ?? [])
      if (ids.length === 1) {
        // Show confirmation popover instead of deleting immediately
        setAnchorEl(event.currentTarget)
      }
    },
    []
  )

  const handleClickMassDelete = useCallback(
    (rowSelectionModel: GridRowSelectionModel, setSelectedMassData: React.Dispatch<React.SetStateAction<Lead[]>>) => {
      const ids = Array.from(rowSelectionModel?.ids ?? [])
      const selectedLeads = leads?.filter(l => ids?.includes(l?.lead_uuid)) ?? []
      setSelectedMassData(selectedLeads)
      setIsMassDeleteOpen(true)
    },
    [leads]
  )

  const handleConfirmDelete = useCallback(
    (setIsDeleting: React.Dispatch<React.SetStateAction<boolean>>) => {
      if (selectedLead) {
        setIsDeleting(true)

        // Close the popover first to avoid anchor element issues
        setAnchorEl(null)

        // Optimistically remove from cache
        const previousLeads = queryClient.getQueryData<Lead[]>(['leads'])
        if (previousLeads) {
          queryClient.setQueryData<Lead[]>(
            ['leads'],
            previousLeads.filter(lead => lead.lead_uuid !== selectedLead.lead_uuid)
          )
        }

        const params = {
          endpoint: `/aws/delete/leads/${selectedLead.lead_uuid}`,
          table: 'leads',
        }
        deleteItem(params, {
          onSuccess: (message: string) => {
            setSelectedLeads([])
            setSelectedLead(null)
            setSelectionModel({ ids: new Set(), type: 'include' })
            showSnackbar(message, 'success')
            setIsDeleting(false)
            // Invalidate to ensure consistency
            queryClient.invalidateQueries({ queryKey: ['leads'] })
          },
          onError: (error: any) => {
            // Rollback on error
            if (previousLeads) {
              queryClient.setQueryData(['leads'], previousLeads)
            }
            showSnackbar(error.message, 'error')
            setIsDeleting(false)
          },
        })
      }
    },
    [selectedLead, deleteItem, showSnackbar, queryClient]
  )

  const handleMassDelete = useCallback(
    (
      rowSelectionModel: GridRowSelectionModel,
      setRowSelectionModel: React.Dispatch<React.SetStateAction<GridRowSelectionModel>>
    ) => {
      setIsDeleting(true)
      const ids = Array.from(rowSelectionModel?.ids ?? [])

      if (!ids.length) {
        setIsDeleting(false)
        return
      }

      const leadDeleteItems = ids.map(id => ({ lead_uuid: id as string }))

      batchDeleteLeads(leadDeleteItems, {
        onSuccess: () => {
          showSnackbar(`${ids.length} lead(s) deleted successfully`, 'success')
          setRowSelectionModel({ ids: new Set(), type: 'include' })
          setSelectionModel({ ids: new Set(), type: 'include' })
          setSelectedLead(null)
          setSelectedLeads([])
          setSelectedMassData([])
          setIsMassDeleteOpen(false)
          setIsDeleting(false)
        },
        onError: (error: any) => {
          showSnackbar(error?.message || 'Failed to delete leads', 'error')
          setIsMassDeleteOpen(false)
          setIsDeleting(false)
        },
      })
    },
    [batchDeleteLeads, showSnackbar]
  )

  const handleClosePopover = useCallback(() => {
    setAnchorEl(null)
    // Clear selection using the grid API
    if (apiRef.current) {
      apiRef.current.setRowSelectionModel({ ids: new Set(), type: 'include' })
    }
    // Update local state
    setSelectionModel({ ids: new Set(), type: 'include' })
    setSelectedLead(null)
    setSelectedLeads([])
    setSelectedMassData([])
    setMassUpdateData({})
  }, [apiRef])

  const handleCloseMassDeletePopover = useCallback(() => {
    setIsMassDeleteOpen(false)
  }, [])

  const handleLeadClick = useCallback((lead: Lead) => {
    setSelectedLead(lead)
    setDrawerOpen(true)
  }, [])

  const columns = useMemo(() => renderLeadsColumns(density, handleLeadClick, users), [density, handleLeadClick, users])

  const handleRowSelectionModelChange = useCallback(
    (newSelectionModel: GridRowSelectionModel) => {
      const newIds = Array.from(newSelectionModel?.ids ?? [])
      const prevIds = Array.from(selectionModel?.ids ?? [])

      // Avoid state churn if unchanged
      if (newIds.length === prevIds.length && newIds.every((id, i) => id === prevIds[i])) return

      setSelectionModel(newSelectionModel)

      if (newIds.length === 0) {
        setSelectedLead(null)
        setSelectedLeads([])
        return
      }

      if (newIds.length > 1) {
        const selectedLeads = leads?.filter(l => newIds?.includes(l?.lead_uuid)) ?? []
        setSelectedLeads(selectedLeads)
        return
      }

      if (newIds.length === 1) {
        const singleId = newIds[0]
        const lead = leads?.find(l => (l.lead_uuid || l.id) === singleId) ?? null
        setSelectedLead(lead)
        setSelectedLeads(lead ? [lead] : [])
        return
      }

      setSelectedLead(null)
      setSelectedLeads([])
    },
    [selectionModel, leads]
  )

  const totalRowCount = leads ? leads.length : 0

  const changeDensity = useCallback((newDensity: GridDensity) => {
    setDensity(newDensity)
  }, [])
  const defaultColumnWidths = Object.fromEntries(
    columns
      .filter(col => typeof col.width === 'number') // only include columns that define a width
      .map(col => [col.field, col.width])
  )
  const footerProps = useMemo(
    () => ({
      users,
      selectedData: selectedLead,
      selectedMassData,
      setSelectedMassData,
      massUpdateData,
      rowCount: totalRowCount,
      onChange: handleMassUpdateChange,
      onUpdate: handleUpdate,
      onClear: handleClearMassUpdate,
      onClickDelete: handleDeleteClick,
      onClickMassDelete: handleClickMassDelete,
      onDelete: handleConfirmDelete,
      onMassDelete: handleMassDelete,
      isDeleting,
      onClosePopover: handleClosePopover,
      onCloseMassDeletePopover: handleCloseMassDeletePopover,
      leads,
      anchorEl,
      customPosition: null,
      isMassDeleteOpen,
    }),
    [
      users,
      selectedLead,
      selectedMassData,
      massUpdateData,
      totalRowCount,
      handleMassUpdateChange,
      handleUpdate,
      handleClearMassUpdate,
      handleDeleteClick,
      handleClickMassDelete,
      handleConfirmDelete,
      handleMassDelete,
      isDeleting,
      handleClosePopover,
      handleCloseMassDeletePopover,
      leads,
      anchorEl,
      isMassDeleteOpen,
    ]
  )

  const FooterWrapper = ({ rowSelectionModel }: { rowSelectionModel: GridRowSelectionModel }) => {
    return <LeadsGridFooter {...footerProps} rowSelectionModel={rowSelectionModel} />
  }

  return (
    <>
      <GlobalDataGrid
        columns={columns}
        rows={leads}
        apiRef={apiRef}
        context={'leads'}
        defaultFilterModel={defaultFilterModel}
        defaultVisibilityModel={defaultVisibilityModel}
        defaultSortModel={defaultSortModel}
        defaultGridDensity={'standard'}
        defaultColumnWidths={defaultColumnWidths}
        onChangeDensity={changeDensity}
        onRowSelectionModelChange={handleRowSelectionModelChange}
        onProcessRowUpdate={processRowUpdate}
        toolbarComponent={GlobalGridToolbar}
        FooterComponent={FooterWrapper}
        treeData={false}
        isLoading={isUpdating || isLeadsLoading}
        gridContainerSx={{
          position: 'relative',
          ...gridContainerStyles,
          pt: pinned ? '60px' : 0.8,
          transition: 'all 0.3s ease',
          height: '98%',
        }}
      />

      {drawerOpen && <LeadDrawer lead={selectedLead} open={drawerOpen} onClose={() => setDrawerOpen(false)} />}
      {/* Single delete confirmation popover */}
      <LeadsPopover
        id="delete-lead-popover"
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        handleConfirmDelete={() => handleConfirmDelete(setIsDeleting)}
        selectedData={selectedLead}
        selectionCount={1}
      />
    </>
  )
}

export default LeadsPage
